import { format } from 'date-fns';
import { ColumnType } from '@dynamika-shared/ui/Table/Table.types';
import { notificationTypesReadable } from '@dynamika-shared/types/subscription';
import { Tooltip } from '@dynamika-shared/ui/Tooltip/Tooltip';
import { Avatar } from 'src/components/Avatar/Avatar';
import {
  NotificationHistoryItem,
} from '@restore/clinicalTrialNotificationHistory/clinicalTrialNotificationHistory.types';
import { RECIPIENT_TYPE_LABEL } from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/constants';
import { StatusPill } from 'src/pages/ClinicalTrial/ClinicalTrialNotifications/components/StatusPill';

const loadingSkeleton = (width: string) => (
  <div className={`h-2 ${width} animate-pulse rounded-[5px] bg-brightGray`} />
);

const cellText = (value?: string | null) => (
  <span className='font-sans text-1624 font-medium text-black whitespace-nowrap'>
    {value || '-'}
  </span>
);

const truncate = (value: string, max = 14) =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`;

export type MakeNotificationHistoryColumnsArgs = {
  clinicalTrialName?: string;
};

export const makeNotificationHistoryColumns = ({
  clinicalTrialName,
}: MakeNotificationHistoryColumnsArgs): ColumnType<NotificationHistoryItem>[] => [
  {
    name: 'messageId',
    header: () => 'Notification ID',
    loading: () => loadingSkeleton('w-[16rem]'),
    getValue: row => (
      <span
        className='block max-w-[16rem] truncate font-sans text-1624 font-medium text-black'
        title={row.messageId ?? row.id}
      >
        {row.messageId ?? row.id ?? '-'}
      </span>
    ),
    hasSorting: true,
    thClassName: 'max-w-[16rem] w-[16rem] whitespace-nowrap',
    tdClassName: 'max-w-[16rem]',
  },
  {
    name: 'notificationType',
    header: () => 'Event Type',
    loading: () => loadingSkeleton('w-[16rem]'),
    getValue: row => cellText(notificationTypesReadable[row.notificationType]),
    hasSorting: true,
    thClassName: 'w-[16rem] min-w-[16rem]',
  },
  {
    name: 'deliveryStatus',
    header: () => 'Status',
    loading: () => loadingSkeleton('w-[10rem]'),
    getValue: row => <StatusPill status={row.deliveryStatus} />,
    hasSorting: true,
    thClassName: 'w-[10rem] min-w-[10rem]',
  },
  {
    name: 'clinicalTrial',
    header: () => 'Clinical Trial',
    loading: () => loadingSkeleton('w-[18rem]'),
    getValue: () => cellText(clinicalTrialName),
    thClassName: 'w-[18rem] min-w-[18rem]',
  },
  {
    name: 'patientCode',
    header: () => 'Subject Code',
    loading: () => loadingSkeleton('w-[18rem]'),
    getValue: row => cellText(row.patientCode),
    hasSorting: true,
    thClassName: 'w-[18rem] min-w-[18rem]',
  },
  {
    name: 'siteCode',
    header: () => 'Subject\'s Site',
    loading: () => loadingSkeleton('w-[14rem]'),
    getValue: row => {
      const withSiteCode = row.siteCode;
      return cellText(withSiteCode ? row.siteCode : '-');
    },
    hasSorting: true,
    thClassName: 'w-[14rem] min-w-[14rem]',
  },
  {
    name: 'recipientName',
    header: () => 'Recipient',
    loading: () => loadingSkeleton('w-[22rem]'),
    getValue: row => {
      const hasName = !!row.recipientName;
      const email = row.recipientEmail ?? '';
      if (!hasName && !email) {
        return (
          <span className='font-sans text-1624 font-medium text-black'>-</span>
        );
      }
      if (!hasName) {
        return (
          <Tooltip
            when={email.length > 28}
            title={<span className='text-black'>{email}</span>}
          >
            <span className='font-sans text-1624 font-medium text-black'>
              {truncate(email, 28)}
            </span>
          </Tooltip>
        );
      }
      return (
        <div className='flex items-center gap-1.5'>
          <div className='flex h-[3.2rem] w-[3.2rem] flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-ghostWhite'>
            <Avatar
              className='text-black bg-brightGray'
              userName={row.recipientName ?? ''}
            />
          </div>
          <div className='flex flex-col'>
            <span className='font-sans text-1616 font-semibold text-black'>
              {row.recipientName}
            </span>
            {email && (
              <span className='font-nunito text-1216 text-dimGray'>
                {email}
              </span>
            )}
          </div>
        </div>
      );
    },
    hasSorting: true,
    thClassName: 'w-[22rem] min-w-[22rem]',
  },
  {
    name: 'recipientType',
    header: () => 'Recipient Type',
    loading: () => loadingSkeleton('w-[16rem]'),
    getValue: row =>
      cellText(
        row.recipientType ? RECIPIENT_TYPE_LABEL[row.recipientType] : undefined
      ),
    hasSorting: true,
    thClassName: 'w-[16rem] min-w-[16rem]',
  },
  {
    name: 'dispatchedAt',
    header: () => 'Date & Time',
    loading: () => loadingSkeleton('w-[18rem]'),
    getValue: row => {
      if (!row.dispatchedAt) return cellText();
      const date = new Date(row.dispatchedAt);
      return (
        <div className='flex items-center gap-0.5 font-sans text-1624 font-medium'>
          <span className='text-black'>{format(date, 'dd-MMM-yyyy')}</span>
          <span className='text-dimGray'>•</span>
          <span className='text-dimGray'>{format(date, 'HH:mm:ss')}</span>
        </div>
      );
    },
    hasSorting: true,
    thClassName: 'w-[18rem] min-w-[18rem]',
  },
];
